import React, { useState, useEffect, useContext, createContext } from 'react';
import { getHostingApp } from '../utils/getHostingApp';
import { getServiceConfig } from '../config/serviceConfig';
import { getDisplayConfig } from '../config/displayConfig';
import { getFeatureConfig } from '../config/featureConfig';
import { getPayloadConfig } from '../config/payloadConfig';
export const ConfigContext = createContext({
  resolvedConfig: undefined,
  resolvedDisplay: undefined,
  resolvedFeatures: undefined,
  resolvedPayload: undefined
});
export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};
/**
 * ConfigProvider is a utility component that resolves config from
 * the prop viewType and makes it available through context.
 * @public
 */
export const ConfigProvider = ({
  children,
  customerName,
  isChatBoxOpened,
  locale,
  startingChatMessagesPayload,
  viewType: viewTypeProp,
  webstats,
  logAnalytics
}) => {
  // Use the provided viewType if available, otherwise fall back to getHostingApp()
  const viewTypeFromWebApp = viewTypeProp || getHostingApp();
  const [resolvedConfig, setResolvedConfig] = useState(undefined);
  const [resolvedDisplay, setResolvedDisplay] = useState(undefined);
  const [resolvedFeatures, setResolvedFeatures] = useState(undefined);
  const [resolvedPayload, setResolvedPayload] = useState(undefined);
  useEffect(() => {
    const processConfig = viewTypeFromWebApp => {
      const isMobile = typeof (webstats === null || webstats === void 0 ? void 0 : webstats.isMobile) === 'boolean' ? webstats.isMobile : false;
      const payload = getPayloadConfig({
        isChatBoxOpened,
        customerName,
        locale,
        startingChatMessagesPayload,
        viewType: viewTypeFromWebApp,
        webstats
      });
      setResolvedPayload(payload);
      // Select the desired config.
      setResolvedConfig(getServiceConfig(viewTypeFromWebApp, isMobile, customerName, logAnalytics));
      // Select the desired style.
      setResolvedDisplay(getDisplayConfig(viewTypeFromWebApp, isMobile));
      // Resolve which features to enable.
      setResolvedFeatures(getFeatureConfig(viewTypeFromWebApp));
    };
    processConfig(viewTypeFromWebApp);
  }, [isChatBoxOpened, viewTypeFromWebApp, customerName, webstats]);
  // Wait until all configs are loaded before rendering children
  if (!resolvedConfig || !resolvedDisplay || !resolvedFeatures || !resolvedPayload) {
    return null; // Or return a loading indicator
  }
  // Provide the resolved configurations through context
  return React.createElement(ConfigContext.Provider, {
    value: {
      resolvedConfig,
      resolvedDisplay,
      resolvedFeatures,
      resolvedPayload
    }
  }, typeof children === 'function' ? children(resolvedConfig, resolvedDisplay, resolvedFeatures, resolvedPayload) : children);
};